import { useMemo, useRef, useState } from 'react'

import { day, money, moneyAxis } from '@/lib/format'
import type { DailyPoint } from '@/types/api'

/**
 * Day by day, as two small charts on one shared time axis: what was earned,
 * and how many users were onboarded. Two measures with different scales get
 * two charts rather than two y-axes. One hover column drives both, so a day
 * reads as one tooltip.
 */
export function DailyChart({ daily }: { daily: DailyPoint[] }) {
  const [hover, setHover] = useState<number | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const n = daily.length

  const maxEarned = useMemo(() => niceMax(Math.max(0, ...daily.map((d) => d.earnedCents)), 100), [daily])
  const maxUsers = useMemo(() => niceMax(Math.max(0, ...daily.map((d) => d.usersOnboarded)), 2), [daily])

  if (n === 0) return null

  const onMove = (event: React.PointerEvent) => {
    const rect = wrap.current?.getBoundingClientRect()
    if (!rect) return
    const x = event.clientX - rect.left - AXIS_W
    const plot = rect.width - AXIS_W
    const index = Math.floor((x / plot) * n)
    setHover(index >= 0 && index < n ? index : null)
  }

  const point = hover != null ? daily[hover] : null
  const ticks = labelIndexes(n)

  return (
    <div ref={wrap} className="relative select-none" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
      <Bars title="Earned" values={daily.map((d) => d.earnedCents)} max={maxEarned} format={moneyAxis} height={150} hover={hover} fill="rgb(var(--brand))" />
      <Bars title="Users onboarded" values={daily.map((d) => d.usersOnboarded)} max={maxUsers} format={(v) => (Number.isInteger(v) ? String(v) : '')} height={96} hover={hover} fill="rgb(var(--info))" className="mt-4" />

      {/* Shared x-axis */}
      <div className="relative mt-1.5 h-4 text-[11px] text-ink-faint" style={{ marginLeft: AXIS_W }}>
        {ticks.map((i) => (
          <span key={i} className="num absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((i + 0.5) / n) * 100}%` }}>
            {day(daily[i]!.date)}
          </span>
        ))}
      </div>

      {point && hover != null && (
        <div
          role="tooltip"
          className="pointer-events-none absolute top-0 z-10 min-w-[150px] rounded-field border border-line bg-surface-raised px-3 py-2 text-[12.5px] shadow-float"
          style={{
            left: `calc(${AXIS_W}px + (100% - ${AXIS_W}px) * ${(hover + 0.5) / n})`,
            transform: hover > n / 2 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
          }}
        >
          <p className="font-semibold text-ink">{day(point.date)}</p>
          <p className="mt-1 flex items-center justify-between gap-4 text-ink-soft">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-[2px] bg-brand" />Earned</span>
            <span className="num font-medium text-ink">{money(point.earnedCents)}</span>
          </p>
          <p className="flex items-center justify-between gap-4 text-ink-soft">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-[2px] bg-info" />Users</span>
            <span className="num font-medium text-ink">{point.usersOnboarded}</span>
          </p>
          <p className="flex items-center justify-between gap-4 text-ink-muted">
            <span className="pl-3.5">Customers paid</span>
            <span className="num">{money(point.paidCents)}</span>
          </p>
        </div>
      )}
    </div>
  )
}

const AXIS_W = 44

function Bars({
  title,
  values,
  max,
  format,
  height,
  hover,
  fill,
  className,
}: {
  title: string
  values: number[]
  max: number
  format: (value: number) => string
  height: number
  hover: number | null
  fill: string
  className?: string
}) {
  const n = values.length
  // Viewbox in "slots": each day is 10 units wide, the bar 7 of them (a 2px-ish gap at typical widths).
  const W = n * 10
  const total = values.reduce((sum, v) => sum + v, 0)
  return (
    <figure className={className} aria-label={`${title} per day`}>
      <figcaption className="mb-1.5 flex items-baseline justify-between text-[12px] font-medium text-ink-muted">
        <span>{title}</span>
        <span className="num text-ink-faint">{title === 'Earned' ? money(total) : total} total</span>
      </figcaption>
      <div className="flex">
        <div className="relative shrink-0 text-right text-[11px] text-ink-faint" style={{ width: AXIS_W, height }}>
          <span className="num absolute right-2 top-0 -translate-y-1/2">{format(max)}</span>
          <span className="num absolute right-2 top-1/2 -translate-y-1/2">{format(max / 2)}</span>
          <span className="num absolute bottom-0 right-2 translate-y-1/2">{format(0)}</span>
        </div>
        <svg className="block min-w-0 flex-1 overflow-visible" viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none" style={{ height }} role="img" aria-hidden="true">
          {[0, 0.5, 1].map((f) => (
            <line key={f} x1={0} x2={W} y1={height * f} y2={height * f} stroke="rgb(var(--line))" strokeWidth={1} vectorEffect="non-scaling-stroke" strokeDasharray={f === 1 ? undefined : '3 3'} />
          ))}
          {hover != null && <rect x={hover * 10} y={0} width={10} height={height} fill="rgb(var(--ink) / 0.05)" />}
          {values.map((v, i) => {
            const h = max > 0 ? (v / max) * height : 0
            if (h <= 0) return null
            return <rect key={i} x={i * 10 + 1.5} y={height - h} width={7} height={h} rx={n > 60 ? 0.8 : 1.6} fill={fill} opacity={hover == null || hover === i ? 1 : 0.45} />
          })}
        </svg>
      </div>
    </figure>
  )
}

/** A round axis maximum: 1, 2, 5 × 10ⁿ, at least `floor`. */
function niceMax(value: number, floor: number): number {
  const v = Math.max(value, floor)
  const exp = Math.pow(10, Math.floor(Math.log10(v)))
  for (const m of [1, 2, 2.5, 5, 10]) if (m * exp >= v) return m * exp
  return 10 * exp
}

/** About five evenly spaced date labels, always including the last day. */
function labelIndexes(n: number): number[] {
  const count = Math.min(5, n)
  if (count <= 1) return [n - 1]
  const step = (n - 1) / (count - 1)
  return Array.from({ length: count }, (_, k) => Math.round(k * step))
}
